import { useEffect, useRef, useState, type Ref } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { usePdfPageRender } from './hooks/usePdfPageRender';
import { useTextHighlight } from './hooks/useTextHighlight';

interface Props {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  cssWidth: number;
  cssHeight: number;
  cssScale: number;
  /**
   * When true, render unconditionally (used to ensure a soloed page is
   * visible after a programmatic scroll).
   */
  forceRender?: boolean;
  /** Root for IntersectionObserver — the scrollable PDF container. */
  scrollRoot: HTMLElement | null;
  children?: React.ReactNode;
  /** React 19 ref-as-prop: forwarded to the underlying page wrapper `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

/**
 * One PDF page wrapper: reserves vertical space at the page's intrinsic
 * dimensions, lazily renders canvas + text layer when intersecting the
 * scroll viewport (with a generous rootMargin so a small scroll doesn't
 * surface a blank page).
 *
 * The wrapper element is exposed via the forwarded ref so the parent
 * viewer can store it in `pageRefs` for scroll-into-view, and so the
 * per-page annotation overlay (added later) can measure the rendered
 * page box.
 */
export function PdfPage({
  pdf,
  pageNumber,
  cssWidth,
  cssHeight,
  cssScale,
  forceRender,
  scrollRoot,
  children,
  ref: forwardedRef,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textLayerRef = useRef<HTMLDivElement | null>(null);
  const [intersecting, setIntersecting] = useState(false);

  const setRefs = (el: HTMLDivElement | null) => {
    wrapperRef.current = el;
    if (typeof forwardedRef === 'function') {
      forwardedRef(el);
    } else if (forwardedRef) {
      forwardedRef.current = el;
    }
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !scrollRoot) return;
    const io = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting), {
      root: scrollRoot,
      rootMargin: '50%',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [scrollRoot]);

  const shouldRender = intersecting || !!forceRender;

  usePdfPageRender({
    pdf,
    pageNumber,
    cssScale,
    shouldRender,
    canvasRef,
    textLayerRef,
  });

  useTextHighlight({ page: pageNumber, pageElementRef: wrapperRef });

  return (
    <div
      ref={setRefs}
      data-page={pageNumber}
      className="relative bg-white shadow"
      style={{ width: cssWidth, height: cssHeight }}
    >
      <canvas ref={canvasRef} className="block" />
      <div
        ref={textLayerRef}
        className="textLayer"
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          opacity: 1,
          lineHeight: 1,
        }}
      />
      {children}
    </div>
  );
}

PdfPage.displayName = 'PdfPage';
